import { MatDialog } from '@angular/material/dialog';
import { Account, Activity } from '@mitel/cloudlink-sdk';
import { AppInjector } from '../../app-injector';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { SpinnerService } from './spinner.service';
import { WorkflowService } from './workflow.service';
import { AdminService } from 'src/app/core/services/admin.service';
import { EnableWorkflowAccessStatement } from '../constants/productPolicies';
import { IntegPolicyService } from './integ-policy.service';
import { AuthService } from './auth.service';
import { AccountsService } from './accounts.service';

const fileContext = 'IntegWorkflowStudioService';

/**
 * Maps to integrations.ts productCode: "WorkflowStudio",
 */
export class IntegWorkflowStudioService implements IntegrationItemServiceInf {

    readonly flowExecutionScheduler_global = 'global_b8e8d8df-671f-42da-b614-68eebcc830e1';
    readonly licenseUpdate_global = 'global_90dd01b4-ac7d-422a-a8c7-ce38f783c8db';
    readonly flowExecutionClientName = 'FlowExecutionClient';

    public dialog: MatDialog;
    private adminService: AdminService;
    private spinnerService: SpinnerService;
    private workflowService: WorkflowService;
    private policyService: IntegPolicyService;
    private authService: AuthService;
    private accountsService: AccountsService;


    initServices() {
        if (!this.dialog)
            this.dialog = AppInjector.injector.get(MatDialog);

        if (!this.adminService)
            this.adminService = AppInjector.injector.get(AdminService);

        if (!this.workflowService)
            this.workflowService = AppInjector.injector.get(WorkflowService);

        if (!this.spinnerService)
            this.spinnerService = AppInjector.injector.get(SpinnerService);

        if (!this.policyService)
            this.policyService = AppInjector.injector.get(IntegPolicyService);

        if (!this.authService)
            this.authService = AppInjector.injector.get(AuthService);

        if (!this.accountsService)
            this.accountsService = AppInjector.injector.get(AccountsService);
    }

    getConfigurationDialog(setupComplete?: boolean | undefined) {
        return undefined;
    }

    isSetupComplete(account: Account): Promise<boolean> {
        return Promise.resolve(true);
    }

    handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[] | undefined): Promise<any> {
        this.initServices();

        this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
        this.spinnerService.showSpinner();
        if (state === false) {
            return this.deleteIntegration(account);
        }
        else {
            return this.addIntegration(account);
        }
    }

    getStatusString(account: Account): Promise<StatusString> {
        return undefined;
    }

    getConfigButtonLabel(account: Account): Promise<string> {
        return undefined;
    }

    isButtonDisabled(account: Account): Promise<boolean> {
        return Promise.resolve(false);
    }

    canDeleteIntegration(account: Account): Promise<boolean> {
        this.initServices();
        return this.dialog
            .open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_WORKFLOW_STUDIO_CONFIRMATION } })
            .afterClosed().toPromise();
    }

    private addIntegration(account: Account): Promise<any> {
        this.initServices();

        return Promise.all([
            this.adminService.getAccountTags(account.accountId),
            this.cloneWorkflowActivities(account.accountId)
        ]).then(([tags, { activity, licenseActivity }]) => {
            const body = {
                flowExecutionFlowId: activity.id,
                flowExecutionClientId: activity.connectors.cloudlink_identity.id,
                licenseFlowId: licenseActivity.id
            };

            if (tags?.workflow) {
                return this.adminService.updateAccountTag(account.accountId, 'workflow', body);
            }
            else {
                return this.adminService.createAccountTagNoFrills(account.accountId, { workflow: body });
            }
        }).then((result) => {
            return this.policyService.addStatementToPolicy(account, EnableWorkflowAccessStatement).then(policy => {

                if (account['policyId'] === undefined) {
                    let dataChanges = {
                        policyId: policy.policyId,
                    }
                    return this.adminService.updateAccount(account.accountId, dataChanges).then(result => {
                        this.accountsService.mergeAccountWithModel(result);
                        return policy;
                    });
                }
                else
                    return policy;
            }).catch(err => {
                console.error(`${fileContext}.addIntegration: Failed to add/update policy`, err);
                throw err;
            });
        }).then((result) => {
            return this.runFlowExecutionActivity(account.accountId);

        }).catch(err => {
            console.error(`${fileContext}.addIntegration failed`, err);
            throw err;
        });

    }

    private deleteIntegration(account: Account): Promise<any> {
        this.initServices();

        return this.adminService.getAccountTags(account.accountId).then(async tags => {
            if (tags?.workflow?.flowExecutionFlowId) {
                console.log(`${fileContext}.deleteIntegration, removing global`, tags.workflow.flowExecutionFlowId);
                await this.workflowService.deleteActivityById(tags.workflow.flowExecutionFlowId);
            } else {
                console.log(`${fileContext}.deleteIntegration, No Flow Execution workflows to delete`);
            }

            if (tags?.workflow?.licenseFlowId) {
                console.log(`${fileContext}.deleteIntegration, removing global`, tags.workflow.licenseFlowId);
                await this.workflowService.deleteActivityById(tags.workflow.licenseFlowId);
            } else {
                console.log(`${fileContext}.deleteIntegration, No License updater workflow to delete`);
            }

            if (tags?.workflow?.flowExecutionClientId) {
                console.log(`${fileContext}.deleteIntegration, removing client`, tags.workflow.flowExecutionClientId);
                return this.adminService.deleteClientById(tags.workflow.flowExecutionClientId).catch(err => {
                    if (err.statusCode === 404) {
                        return;
                    }
                    console.error(`${fileContext}.deleteIntegration, Failed to remove client ${tags.workflow.flowExecutionClientId}`, err);
                    throw err;
                });
            } else {
                console.log(`${fileContext}.deleteIntegration, No Flow Execution client to delete`);
                return;
            }
        }).then(() => {
            return this.adminService.deleteAccountTag(account.accountId, 'workflow').catch(err => {
                if (err.statusCode === 404) {
                    return;
                }
                console.error(`${fileContext}.deleteIntegration, Failed to delete account tag`, err);
                throw err;
            });
        }).then(() => {
            return this.policyService.removeAccountPolicyIfItExists(account, EnableWorkflowAccessStatement).then(ret => {
                console.info(`${fileContext}.deleteIntegration, Removed account policy for workflow`);
                return true;
            }).catch(err => {
                console.error(`${fileContext}.deleteIntegration, Failed to remove account policy`, err);
                throw err;
            });
        }).catch(err => {
            console.error(`${fileContext}.deleteIntegration failed`, err);
            throw err;
        });
    }

    private async cloneWorkflowActivities(accountId: string): Promise<{ activity: any, licenseActivity: any }> {
        this.initServices();

        try {
            // Create a client that the workflow will run under
            const clientName = `${accountId}-${this.flowExecutionClientName}`;
            const client = await this.adminService.createClient(accountId, clientName, 'ACCOUNT_ADMIN');
            if (!client) {
                throw new Error(`Failed to create client ${clientName}`);
            }

            let activity = await this.workflowService.cloneActivity(this.flowExecutionScheduler_global, 'Cloned Flow Execution Scheduler');
            if (activity) {

                const clientToken = await this.authService.createTokenForClient(client.clientId, client.clientSecret);

                activity = await this.workflowService.updateActivityAttributesUsingTokenProvided(clientToken.access_token, activity.id, [
                    {
                        "op": "REPLACE",
                        "path": "/lock",
                        "value": true
                    },
                    {
                        "op": "REPLACE",
                        "path": "/visibility",
                        "value": "Hidden"
                    },
                    {
                        "op": "REPLACE",
                        "path": "/connectors",
                        "value": {
                            "cloudlink_identity": {
                                "$type": "CloudlinkIdentity",
                                "id": client.clientId
                            }
                        }
                    }
                ]) as Activity;

                let licenseActivity = await this.workflowService.cloneActivity(this.licenseUpdate_global, 'Cloned License updater');
                if (licenseActivity) {

                    licenseActivity = await this.workflowService.updateActivityAttributesUsingTokenProvided(clientToken.access_token, licenseActivity.id, [
                        {
                            "op": "REPLACE",
                            "path": "/connectors",
                            "value": {
                                "cloudlink_identity": {
                                    "$type": "CloudlinkIdentity",
                                    "id": client.clientId
                                }
                            }
                        },
                        {
                            "op": "REPLACE",
                            "path": "/trigger/inputs",
                            "value": {
                                "AccountId": {
                                    "expression": accountId,
                                    "expressionType": "Literal"
                                }
                            }
                        }
                    ]) as Activity;

                    return { activity, licenseActivity };
                }
                throw new Error(`Failed to clone license update activity`);
            }
            throw new Error(`Failed to clone flow execution scheduler activity`);
        } catch (err) {
            throw new Error(`${fileContext}.cloneWorkflowActivities failed`, err)
        }
    }

    private async runFlowExecutionActivity(accountId: string): Promise<any> {
        this.initServices();

        try {
            const tags = await this.adminService.getAccountTags(accountId);
            if (tags?.workflow?.flowExecutionFlowId) {
                console.log(`${fileContext}.runFlowExecutionActivity, executing flow execution workflow`, tags.workflow.flowExecutionFlowId);
                await this.workflowService.executeWorkflowWithNoWait(tags.workflow.flowExecutionFlowId, {});
            } else {
                console.log(`${fileContext}.runFlowExecutionActivity, failed to find flowExecutionFlowId in account tags`);
            }

        } catch (err) {
            throw new Error(`${fileContext}.runFlowExecutionActivity failed`, err)
        }
    }
}